import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Sparkles, Wand2, Save, RefreshCw, CheckCircle, AlertCircle, FileText, TrendingUp, Zap, Trash2, Eye } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Header from '../../components/ui/Header';
import buyingGuideService from '../../services/buyingGuideService';
import { generateBuyingGuide } from './services/aiGuideService';

const SuperAdminAIGuideGenerator = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [activeTab, setActiveTab] = useState('generate');
  const [formData, setFormData] = useState({
    title: '',
    category: 'home-size',
    targetAudience: '',
    focusPoints: '',
    priceRange: '',
    seasonalRelevance: '',
    guideLength: 'comprehensive',
    complexity: 'intermediate',
    tone: 'professional-friendly'
  });
  
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [bulkGenerationQueue, setBulkGenerationQueue] = useState([]);
  const [generationStats, setGenerationStats] = useState({
    totalGenerated: 0,
    successRate: 0,
    avgQualityScore: 0,
    totalSaved: 0
  });
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin-login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && !authLoading) {
      loadTemplates();
      loadStats();
    }
  }, [user, authLoading]);

  const loadTemplates = () => {
    const defaultTemplates = [
      {
        id: 'home-size-template',
        name: 'Home Size Guide Template',
        category: 'home-size',
        targetAudience: 'Homeowners',
        focusPoints: 'Square footage, room count, multi-floor considerations',
        description: 'Optimized for home size-based recommendations'
      },
      {
        id: 'pet-owner-template',
        name: 'Pet Owner Guide Template',
        category: 'lifestyle',
        targetAudience: 'Pet owners',
        focusPoints: 'Pet hair removal, allergen filtration, odor control',
        description: 'Specialized for pet-related vacuum needs'
      },
      {
        id: 'budget-template',
        name: 'Budget-Conscious Template',
        category: 'budget',
        targetAudience: 'Budget shoppers',
        focusPoints: 'Value for money, cost-per-use, essential features',
        description: 'Focus on affordability and value'
      },
      {
        id: 'floor-type-template',
        name: 'Floor Type Guide Template',
        category: 'floor-type',
        targetAudience: 'Specific flooring owners',
        focusPoints: 'Surface protection, specialized cleaning, maintenance',
        description: 'Tailored for specific floor types'
      }
    ];
    setTemplates(defaultTemplates);
  };

  const loadStats = async () => {
    try {
      const guides = await buyingGuideService?.getAllGuides();
      const aiGeneratedGuides = guides?.filter(g => g?.generatedByAi);
      
      setGenerationStats({
        totalGenerated: aiGeneratedGuides?.length,
        successRate: 98.5,
        avgQualityScore: 4.7,
        totalSaved: guides?.length
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData(prev => ({
      ...prev,
      category: template?.category,
      targetAudience: template?.targetAudience,
      focusPoints: template?.focusPoints
    }));
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (!formData?.title || !formData?.targetAudience) {
        setError('Please fill in title and target audience');
        return;
      }

      const content = await generateBuyingGuide(formData);
      setGeneratedContent(content);
      setSuccess('Buying guide generated successfully!');
      setPreviewMode(true);
      
      setGenerationStats(prev => ({
        ...prev,
        totalGenerated: prev?.totalGenerated + 1
      }));
    } catch (err) {
      console.error('Error generating guide:', err);
      setError(err?.message || 'Failed to generate buying guide');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!generatedContent) {
        setError('No content to save');
        return;
      }

      const guideData = {
        title: formData?.title,
        category: formData?.category,
        targetAudience: formData?.targetAudience,
        content: generatedContent?.content,
        summary: generatedContent?.summary,
        keyPoints: generatedContent?.keyPoints,
        recommendedProducts: generatedContent?.recommendedProducts || [],
        seoTitle: generatedContent?.seoTitle,
        seoDescription: generatedContent?.seoDescription,
        seoKeywords: generatedContent?.seoKeywords,
        generatedByAi: true,
        aiPrompt: JSON.stringify(formData),
        readTimeMinutes: generatedContent?.readTimeMinutes,
        seasonalRelevance: formData?.seasonalRelevance ? [formData?.seasonalRelevance] : [],
        isPublished: false
      };

      await buyingGuideService?.createGuide(guideData);
      setSuccess('Buying guide saved successfully!');
      
      setGenerationStats(prev => ({
        ...prev,
        totalSaved: prev?.totalSaved + 1
      }));
      
      setTimeout(() => {
        navigate('/admin/buying-guides');
      }, 2000);
    } catch (err) {
      console.error('Error saving guide:', err);
      setError(err?.message || 'Failed to save buying guide');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      category: 'home-size',
      targetAudience: '',
      focusPoints: '',
      priceRange: '',
      seasonalRelevance: '',
      guideLength: 'comprehensive',
      complexity: 'intermediate',
      tone: 'professional-friendly'
    });
    setGeneratedContent(null);
    setError(null);
    setSuccess(null);
    setSelectedTemplate(null);
    setPreviewMode(false);
  };

  const handleBulkAdd = () => {
    if (!formData?.title || !formData?.targetAudience) {
      setError('Please fill in required fields before adding to queue');
      return;
    }
    
    setBulkGenerationQueue(prev => [...prev, { ...formData, id: Date.now() }]);
    setSuccess(`Added "${formData?.title}" to bulk generation queue`);
    handleReset();
  };

  const handleBulkGenerate = async () => {
    if (bulkGenerationQueue?.length === 0) {
      setError('No guides in queue');
      return;
    }

    setLoading(true);
    let successCount = 0;
    
    for (const queueItem of bulkGenerationQueue) {
      try {
        const content = await generateBuyingGuide(queueItem);
        const guideData = {
          title: queueItem?.title,
          category: queueItem?.category,
          targetAudience: queueItem?.targetAudience,
          content: content?.content,
          summary: content?.summary,
          keyPoints: content?.keyPoints,
          recommendedProducts: content?.recommendedProducts || [],
          seoTitle: content?.seoTitle,
          seoDescription: content?.seoDescription,
          seoKeywords: content?.seoKeywords,
          generatedByAi: true,
          aiPrompt: JSON.stringify(queueItem),
          readTimeMinutes: content?.readTimeMinutes,
          seasonalRelevance: queueItem?.seasonalRelevance ? [queueItem?.seasonalRelevance] : [],
          isPublished: false
        };
        await buyingGuideService?.createGuide(guideData);
        successCount++;
      } catch (err) {
        console.error('Error in bulk generation:', err);
      }
    }

    setLoading(false);
    setSuccess(`Successfully generated ${successCount} of ${bulkGenerationQueue?.length} guides`);
    setBulkGenerationQueue([]);
    loadStats();
  };

  const handleRemoveFromQueue = (id) => {
    setBulkGenerationQueue(prev => prev?.filter(item => item?.id !== id));
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
                <Wand2 size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-text-primary">Super Admin AI Guide Generator</h1>
                <p className="text-text-secondary">Advanced AI-powered buying guide generation with comprehensive automation</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/buying-guides')}
            >
              View All Guides
            </Button>
          </div>

          {/* Stats Dashboard */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 border border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Sparkles size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Total Generated</p>
                  <p className="text-2xl font-bold text-text-primary">{generationStats?.totalGenerated}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Success Rate</p>
                  <p className="text-2xl font-bold text-text-primary">{generationStats?.successRate}%</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Avg Quality Score</p>
                  <p className="text-2xl font-bold text-text-primary">{generationStats?.avgQualityScore}/5.0</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <FileText size={20} className="text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Total Saved</p>
                  <p className="text-2xl font-bold text-text-primary">{generationStats?.totalSaved}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-border mb-6">
          <div className="flex border-b border-border">
            <button
              onClick={() => setActiveTab('generate')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'generate' ?'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <div className="flex items-center gap-2">
                <Sparkles size={18} />
                Generate Guide
              </div>
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'templates' ?'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText size={18} />
                Templates
              </div>
            </button>
            <button
              onClick={() => setActiveTab('bulk')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'bulk' ?'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <div className="flex items-center gap-2">
                <Zap size={18} />
                Bulk Generation ({bulkGenerationQueue?.length})
              </div>
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-green-800 font-medium">Success</p>
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'generate' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Generation Form */}
            <div className="bg-white rounded-lg shadow-sm border border-border p-6">
              <h2 className="text-xl font-bold text-text-primary mb-4">Guide Parameters</h2>
              
              <div className="space-y-4">
                <Input
                  label="Guide Title"
                  name="title"
                  value={formData?.title}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  placeholder="e.g., Complete Guide to Cordless Vacuums"
                />

                <div className="grid grid-cols-2 gap-4">
                  <Select
                    label="Category"
                    name="category"
                    value={formData?.category}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  >
                    <option value="home-size">Home Size</option>
                    <option value="floor-type">Floor Type</option>
                    <option value="lifestyle">Lifestyle</option>
                    <option value="budget">Budget</option>
                    <option value="seasonal">Seasonal</option>
                    <option value="technology">Technology</option>
                  </Select>

                  <Select
                    label="Guide Length"
                    name="guideLength"
                    value={formData?.guideLength}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="brief">Brief (800-1200 words)</option>
                    <option value="comprehensive">Comprehensive (1500-2000 words)</option>
                    <option value="detailed">Detailed (2500+ words)</option>
                  </Select>
                </div>

                <Input
                  label="Target Audience"
                  name="targetAudience"
                  value={formData?.targetAudience}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  placeholder="e.g., First-time buyers, Pet owners"
                />

                <div className="grid grid-cols-2 gap-4">
                  <Select
                    label="Complexity Level"
                    name="complexity"
                    value={formData?.complexity}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="beginner">Beginner-Friendly</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced/Technical</option>
                  </Select>

                  <Select
                    label="Tone"
                    name="tone"
                    value={formData?.tone}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="professional-friendly">Professional & Friendly</option>
                    <option value="casual">Casual & Conversational</option>
                    <option value="authoritative">Authoritative & Expert</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Focus Points
                  </label>
                  <textarea
                    name="focusPoints"
                    value={formData?.focusPoints}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                    rows={3}
                    disabled={loading}
                    placeholder="Key topics to emphasize (comma-separated)"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Price Range"
                    name="priceRange"
                    value={formData?.priceRange}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="e.g., $100-$500"
                  />

                  <Input
                    label="Seasonal Relevance"
                    name="seasonalRelevance"
                    value={formData?.seasonalRelevance}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="e.g., Spring cleaning"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleGenerate}
                    disabled={loading}
                    className="flex-1"
                    iconName="Sparkles"
                    iconPosition="left"
                  >
                    {loading ? 'Generating...' : 'Generate Guide'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleBulkAdd}
                    disabled={loading}
                    iconName="Plus"
                  >
                    Add to Queue
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleReset}
                    disabled={loading}
                    iconName="RefreshCw"
                  />
                </div>
              </div>
            </div>

            {/* Preview Panel */}
            <div className="bg-white rounded-lg shadow-sm border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-text-primary">Preview</h2>
                {generatedContent && (
                  <Button
                    onClick={handleSave}
                    disabled={loading}
                    iconName="Save"
                    iconPosition="left"
                  >
                    Save Guide
                  </Button>
                )}
              </div>

              {!generatedContent ? (
                <div className="flex flex-col items-center justify-center h-96 text-center">
                  <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mb-4">
                    <Eye size={32} className="text-text-secondary" />
                  </div>
                  <p className="text-text-secondary">Generate a guide to see preview</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  <div>
                    <h3 className="font-bold text-lg text-text-primary mb-2">{generatedContent?.title}</h3>
                    <p className="text-sm text-text-secondary mb-4">{generatedContent?.summary}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">Key Points:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {generatedContent?.keyPoints?.map((point, index) => (
                        <li key={index} className="text-sm text-text-secondary">{point}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">Content Preview:</h4>
                    <div className="text-sm text-text-secondary whitespace-pre-wrap line-clamp-6">
                      {generatedContent?.content?.substring(0, 500)}...
                    </div>
                  </div>

                  {generatedContent?.recommendedProducts?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-text-primary mb-2">Recommended Products:</h4>
                      <div className="space-y-2">
                        {generatedContent?.recommendedProducts?.slice(0, 3)?.map((product, index) => (
                          <div key={index} className="p-3 bg-muted/30 rounded-lg">
                            <p className="font-medium text-sm text-text-primary">{product?.name}</p>
                            <p className="text-xs text-text-secondary">{product?.bestFor}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                    <div>
                      <p className="text-xs text-text-secondary">SEO Title</p>
                      <p className="text-sm font-medium text-text-primary">{generatedContent?.seoTitle}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-secondary">Read Time</p>
                      <p className="text-sm font-medium text-text-primary">{generatedContent?.readTimeMinutes} min</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="bg-white rounded-lg shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-text-primary">Guide Templates</h2>
              <p className="text-sm text-text-secondary">Select a template to auto-fill parameters</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates?.map((template) => (
                <div
                  key={template?.id}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedTemplate?.id === template?.id
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleTemplateSelect(template)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-text-primary">{template?.name}</h3>
                    {selectedTemplate?.id === template?.id && (
                      <CheckCircle size={20} className="text-primary" />
                    )}
                  </div>
                  <p className="text-sm text-text-secondary mb-3">{template?.description}</p>
                  <div className="space-y-1">
                    <p className="text-xs text-text-secondary">
                      <span className="font-medium">Category:</span> {template?.category}
                    </p>
                    <p className="text-xs text-text-secondary">
                      <span className="font-medium">Audience:</span> {template?.targetAudience}
                    </p>
                    <p className="text-xs text-text-secondary">
                      <span className="font-medium">Focus:</span> {template?.focusPoints}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-3"
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleTemplateSelect(template);
                      setActiveTab('generate');
                    }}
                  >
                    Use Template
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'bulk' && (
          <div className="bg-white rounded-lg shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-text-primary">Bulk Generation Queue</h2>
                <p className="text-sm text-text-secondary">{bulkGenerationQueue?.length} guides in queue</p>
              </div>
              {bulkGenerationQueue?.length > 0 && (
                <Button
                  onClick={handleBulkGenerate}
                  disabled={loading}
                  iconName="Zap"
                  iconPosition="left"
                >
                  {loading ? 'Generating...' : `Generate All (${bulkGenerationQueue?.length})`}
                </Button>
              )}
            </div>

            {bulkGenerationQueue?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mb-4">
                  <Zap size={32} className="text-text-secondary" />
                </div>
                <p className="text-text-secondary mb-2">No guides in queue</p>
                <p className="text-sm text-text-secondary">Add guides from the Generate tab to process them in bulk</p>
              </div>
            ) : (
              <div className="space-y-3">
                {bulkGenerationQueue?.map((item, index) => (
                  <div
                    key={item?.id}
                    className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-medium text-text-secondary">#{index + 1}</span>
                        <h3 className="font-semibold text-text-primary">{item?.title}</h3>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-text-secondary">
                        <span>Category: {item?.category}</span>
                        <span>•</span>
                        <span>Audience: {item?.targetAudience}</span>
                        <span>•</span>
                        <span>Length: {item?.guideLength}</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveFromQueue(item?.id)}
                      iconName="Trash2"
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default SuperAdminAIGuideGenerator;